import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import { PostFormAction } from '../../Component/PostFormAction'
import ManageTable from '../ManageTable'
import { RequireModule, PageHeader, formatInr, BTN_PRIMARY } from './shared'
import { mediaUrl } from '../../utils/mediaUrl'

const EMPTY = {
  expense_id: '',
  expense_type_id: '',
  amount: '',
  expense_date: '',
}

const MAX_FILES = 10
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.gif,application/pdf,image/*'

function formatExpenseDate(value) {
  if (!value) return '—'
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-')
    return `${d}-${m}-${y}`
  }
  return s
}

function ExpensesPage() {
  const [rows, setRows] = useState([])
  const [types, setTypes] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [showView, setShowView] = useState(false)
  const [viewRow, setViewRow] = useState(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [invoices, setInvoices] = useState([])
  const [receipts, setReceipts] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    const [data, typeData] = await Promise.all([
      GetAction('get', {}, 'expense/list'),
      GetAction('get', { all: 1 }, 'expense_type/list'),
    ])
    if (data?.status === 'success' && Array.isArray(data.response)) {
      setRows(data.response)
      setError('')
    } else {
      setError(data?.message || 'Failed to load expenses.')
      setRows([])
    }
    if (typeData?.status === 'success' && Array.isArray(typeData.response)) {
      setTypes(typeData.response)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const existingCount = useMemo(() => {
    if (!form.expense_id) return 0
    const row = rows.find((r) => String(r.expense_id) === String(form.expense_id))
    return (row?.attachments || []).length
  }, [form.expense_id, rows])

  const newFileCount = invoices.length + receipts.length
  const remainingSlots = Math.max(0, MAX_FILES - existingCount)

  const typeOptions = useMemo(() => {
    const enabled = types.filter((t) => Number(t.status) === 1)
    if (!form.expense_type_id) return enabled
    const current = types.find(
      (t) => String(t.expense_type_id) === String(form.expense_type_id)
    )
    if (current && !enabled.some((t) => t.expense_type_id === current.expense_type_id)) {
      return [current, ...enabled]
    }
    return enabled
  }, [types, form.expense_type_id])

  const closeForm = () => {
    setShowForm(false)
    setInvoices([])
    setReceipts([])
  }

  const openNew = () => {
    const today = new Date()
    const yyyy = today.getFullYear()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    setForm({ ...EMPTY, expense_date: `${yyyy}-${mm}-${dd}` })
    setInvoices([])
    setReceipts([])
    setShowForm(true)
    setMessage(null)
  }

  const openView = (row) => {
    setViewRow(row)
    setShowView(true)
  }

  const openEdit = (row) => {
    const date = String(row.expense_date || '')
    setForm({
      expense_id: row.expense_id != null ? String(row.expense_id) : '',
      expense_type_id:
        row.expense_type_id != null ? String(row.expense_type_id) : '',
      amount: row.amount ?? '',
      expense_date: /^\d{4}-\d{2}-\d{2}/.test(date) ? date.slice(0, 10) : '',
    })
    setInvoices([])
    setReceipts([])
    setShowView(false)
    setShowForm(true)
    setMessage(null)
  }

  const onDelete = async (row) => {
    const label = row.type_name || 'this expense'
    const amount = row.amount != null ? ` (${formatInr(row.amount)})` : ''
    if (!window.confirm(`Delete ${label}${amount}? This cannot be undone.`)) return

    setMessage(null)
    const data = await PostAction(
      'post',
      { expense_id: row.expense_id },
      'expense/delete'
    )
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Expense deleted.' })
      if (String(viewRow?.expense_id) === String(row.expense_id)) {
        setShowView(false)
        setViewRow(null)
      }
      await load()
    } else {
      setMessage({ error: true, text: data?.message || 'Failed to delete expense.' })
    }
  }

  const editingAttachments = useMemo(() => {
    if (!form.expense_id) return []
    const row = rows.find((r) => String(r.expense_id) === String(form.expense_id))
    return row?.attachments || []
  }, [form.expense_id, rows])

  const removeExistingAttachment = async (att) => {
    if (!window.confirm(`Remove ${att.original_name || 'this file'}?`)) return
    const data = await PostAction(
      'post',
      { expense_attachment_id: att.expense_attachment_id },
      'expense/deleteAttachment'
    )
    if (data?.status === 'success') {
      await load()
    } else {
      setMessage({ error: true, text: data?.message || 'Failed to remove attachment.' })
    }
  }

  const pickFiles = (kind, fileList) => {
    const incoming = Array.from(fileList || [])
    setMessage(null)
    const other = kind === 'invoices' ? receipts.length : invoices.length
    const current = kind === 'invoices' ? invoices : receipts
    const allowed = remainingSlots - other
    if (incoming.length + current.length > allowed) {
      setMessage({
        error: true,
        text: `You can attach at most ${MAX_FILES} files per expense (${remainingSlots} remaining).`,
      })
      return
    }
    if (kind === 'invoices') setInvoices((prev) => [...prev, ...incoming])
    else setReceipts((prev) => [...prev, ...incoming])
  }

  const removeNewFile = (kind, index) => {
    if (kind === 'invoices') setInvoices((prev) => prev.filter((_, i) => i !== index))
    else setReceipts((prev) => prev.filter((_, i) => i !== index))
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!form.expense_type_id) {
      setMessage({ error: true, text: 'Please select an expense type.' })
      return
    }
    if (!form.amount || Number(form.amount) <= 0) {
      setMessage({ error: true, text: 'Amount must be greater than zero.' })
      return
    }
    if (!form.expense_date) {
      setMessage({ error: true, text: 'Please select the expense date.' })
      return
    }
    if (newFileCount + existingCount > MAX_FILES) {
      setMessage({ error: true, text: `You can attach at most ${MAX_FILES} files.` })
      return
    }

    setSaving(true)
    setMessage(null)
    const fd = new FormData()
    if (form.expense_id) fd.append('expense_id', form.expense_id)
    fd.append('expense_type_id', form.expense_type_id)
    fd.append('amount', String(form.amount))
    fd.append('expense_date', form.expense_date)
    invoices.forEach((file) => fd.append('invoices[]', file))
    receipts.forEach((file) => fd.append('receipts[]', file))

    const data = await PostFormAction('post', fd, 'expense/save')
    const ok = data?.status === 'success'
    if (ok) {
      setMessage({ error: false, text: data.message || 'Expense saved.' })
      closeForm()
      await load()
    } else {
      setMessage({
        error: true,
        text: data?.message || data?.data?.message || 'Failed to save expense.',
      })
    }
    setSaving(false)
  }

  const columns = [
    { key: 'index', label: '#', width: '60px' },
    {
      key: 'expense_date',
      label: 'Date',
      render: (row) => formatExpenseDate(row.expense_date),
    },
    {
      key: 'type_name',
      label: 'Expense type',
      render: (row) => row.type_name || '—',
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (row) => formatInr(row.amount),
    },
    {
      key: 'attachment_count',
      label: 'Attachments',
      render: (row) => row.attachment_count ?? (row.attachments || []).length,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="d-flex flex-wrap gap-1">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => openView(row)}
          >
            View
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => openEdit(row)}
          >
            Edit
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => onDelete(row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ]

  const viewInvoices = (viewRow?.attachments || []).filter(
    (a) => a.attachment_type === 'invoice'
  )
  const viewReceipts = (viewRow?.attachments || []).filter(
    (a) => a.attachment_type === 'receipt'
  )

  return (
    <RequireModule moduleKey="expenses">
      <PageHeader
        title="Expenses"
        subtitle="Record expenses with invoices and payment receipts. Latest entries appear first."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={BTN_PRIMARY}
            onClick={openNew}
          >
            Add Expense
          </button>
        }
      />
      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      <div className="manage-card mb-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <ManageTable
            columns={columns}
            rows={rows}
            searchKeys={['type_name', 'amount', 'expense_date']}
            emptyMessage="No expenses yet. Add the first one."
          />
        )}
      </div>

      <Modal
        show={showForm}
        onHide={closeForm}
        backdrop="static"
        centered
        scrollable
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{form.expense_id ? 'Edit Expense' : 'Add Expense'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message?.error && showForm ? (
            <div className="manage-alert manage-alert-err mb-3">{message.text}</div>
          ) : null}
          <form id="manage-expense-form" onSubmit={onSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="ex_type">
                  Expense type
                </label>
                <select
                  id="ex_type"
                  className="form-control"
                  required
                  value={form.expense_type_id}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, expense_type_id: e.target.value }))
                  }
                >
                  <option value="">Select category</option>
                  {typeOptions.map((t) => (
                    <option key={t.expense_type_id} value={String(t.expense_type_id)}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="ex_amount">
                  Amount
                </label>
                <input
                  id="ex_amount"
                  type="number"
                  min="1"
                  step="0.01"
                  className="form-control"
                  required
                  value={form.amount}
                  onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="ex_date">
                  Date of expense
                </label>
                <input
                  id="ex_date"
                  type="date"
                  className="form-control"
                  required
                  value={form.expense_date}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, expense_date: e.target.value }))
                  }
                />
              </div>
            </div>

            {form.expense_id && editingAttachments.length ? (
              <div className="mb-3">
                <div className="form-label">Existing attachments</div>
                <ul className="manage-file-list">
                  {editingAttachments.map((att) => (
                    <li key={att.expense_attachment_id}>
                      <a
                        href={mediaUrl(att.file_url || att.file_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {att.attachment_type === 'receipt' ? 'Receipt' : 'Invoice'}:{' '}
                        {att.original_name || 'File'}
                      </a>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger"
                        onClick={() => removeExistingAttachment(att)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <p className="text-muted small mb-2">
              Upload invoices and payment receipts (PDF or images). Maximum {MAX_FILES}{' '}
              files in total
              {remainingSlots < MAX_FILES ? ` — ${remainingSlots} remaining` : ''}.
            </p>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="ex_invoices">
                  Invoice
                </label>
                <input
                  id="ex_invoices"
                  type="file"
                  className="form-control"
                  accept={ACCEPT}
                  multiple
                  onChange={(e) => {
                    pickFiles('invoices', e.target.files)
                    e.target.value = ''
                  }}
                />
                {invoices.length ? (
                  <ul className="manage-file-list">
                    {invoices.map((file, i) => (
                      <li key={`inv-${file.name}-${i}`}>
                        <span>{file.name}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger"
                          onClick={() => removeNewFile('invoices', i)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="ex_receipts">
                  Payment receipts
                </label>
                <input
                  id="ex_receipts"
                  type="file"
                  className="form-control"
                  accept={ACCEPT}
                  multiple
                  onChange={(e) => {
                    pickFiles('receipts', e.target.files)
                    e.target.value = ''
                  }}
                />
                {receipts.length ? (
                  <ul className="manage-file-list">
                    {receipts.map((file, i) => (
                      <li key={`rcpt-${file.name}-${i}`}>
                        <span>{file.name}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger"
                          onClick={() => removeNewFile('receipts', i)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={closeForm}>
            Cancel
          </button>
          <button
            type="submit"
            form="manage-expense-form"
            className="btn btn-primary"
            style={BTN_PRIMARY}
            disabled={saving}
          >
            {saving ? 'Saving…' : form.expense_id ? 'Update Expense' : 'Save Expense'}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showView} onHide={() => setShowView(false)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Expense details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewRow ? (
            <>
              <p className="mb-1">
                <strong>Type:</strong> {viewRow.type_name || '—'}
              </p>
              <p className="mb-1">
                <strong>Amount:</strong> {formatInr(viewRow.amount)}
              </p>
              <p className="mb-3">
                <strong>Date:</strong> {formatExpenseDate(viewRow.expense_date)}
              </p>
              <h3 className="h6">Invoices</h3>
              {viewInvoices.length === 0 ? (
                <p className="text-muted">No invoices uploaded.</p>
              ) : (
                <ul className="manage-file-list">
                  {viewInvoices.map((att) => (
                    <li key={att.expense_attachment_id}>
                      <a
                        href={mediaUrl(att.file_url || att.file_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {att.original_name || 'Invoice'}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              <h3 className="h6 mt-3">Payment receipts</h3>
              {viewReceipts.length === 0 ? (
                <p className="text-muted mb-0">No payment receipts uploaded.</p>
              ) : (
                <ul className="manage-file-list mb-0">
                  {viewReceipts.map((att) => (
                    <li key={att.expense_attachment_id}>
                      <a
                        href={mediaUrl(att.file_url || att.file_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {att.original_name || 'Receipt'}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          {viewRow ? (
            <>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => openEdit(viewRow)}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => onDelete(viewRow)}
              >
                Delete
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowView(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </RequireModule>
  )
}

export default ExpensesPage
